import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, TextField, TextFieldProps } from '@mui/material';
import { PHONE_COUNTRIES_LATAM } from '../../utils/constants/phoneCountries';
import {
  buildPhoneValue,
  getPhoneCountryByIso,
  getPhoneLengthHint,
  sanitizePhoneDigits,
  splitPhoneValue,
} from '../../utils/phone';
import PhoneCountryFlag from './PhoneCountryFlag';

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Aplica a ambos campos (selector de país y número). */
  size?: TextFieldProps['size'];
  nationalNumberLabel?: string;
  nationalNumberPlaceholder?: string;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange'>;
}

const FLAG_ROW_STYLE = { width: 26, height: 'auto', display: 'block' } as const;

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  disabled = false,
  size = 'medium',
  nationalNumberLabel = 'Número',
  nationalNumberPlaceholder = 'Ingrese número',
  textFieldProps,
}) => {
  const parsed = useMemo(() => splitPhoneValue(value), [value]);
  const [countryIso, setCountryIso] = useState(parsed.countryIso);
  const [nationalNumber, setNationalNumber] = useState(parsed.nationalNumber);

  useEffect(() => {
    setCountryIso(parsed.countryIso);
    setNationalNumber(parsed.nationalNumber);
  }, [parsed.countryIso, parsed.nationalNumber]);

  const selectedCountry = getPhoneCountryByIso(countryIso);

  const handleCountryChange = (nextCountryIso: string) => {
    setCountryIso(nextCountryIso);
    onChange(buildPhoneValue(nextCountryIso, nationalNumber));
  };

  const handleNumberChange = (rawValue: string) => {
    const digits = sanitizePhoneDigits(rawValue);
    const maxDigits = selectedCountry.nationalNumberLength;
    const trimmed = digits.slice(0, maxDigits);
    setNationalNumber(trimmed);
    onChange(buildPhoneValue(countryIso, trimmed));
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <TextField
        select
        size={size}
        value={countryIso}
        onChange={(e) => handleCountryChange(e.target.value)}
        disabled={disabled}
        sx={{ minWidth: 76, maxWidth: 88 }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            const iso = String(selected || countryIso);
            const meta = getPhoneCountryByIso(iso);
            return (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 0.125,
                }}
                title={`${meta.name} (${meta.dialCode})`}
                aria-label={`${meta.name}, ${meta.dialCode}`}
              >
                <PhoneCountryFlag iso2={iso} style={FLAG_ROW_STYLE} />
              </Box>
            );
          },
          MenuProps: {
            PaperProps: {
              sx: { minWidth: 260 },
            },
          },
        }}
      >
        {PHONE_COUNTRIES_LATAM.map((country) => (
          <MenuItem key={country.iso2} value={country.iso2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <PhoneCountryFlag iso2={country.iso2} style={FLAG_ROW_STYLE} />
              <Box component="span" sx={{ flex: 1 }}>
                {country.name}
              </Box>
              <Box component="span" sx={{ color: 'text.secondary', typography: 'body2' }}>
                {country.dialCode}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        size={size}
        value={nationalNumber}
        onChange={(e) => handleNumberChange(e.target.value)}
        label={nationalNumberLabel}
        placeholder={`${nationalNumberPlaceholder} (${getPhoneLengthHint(selectedCountry)} dígitos)`}
        inputProps={{
          inputMode: 'numeric',
          pattern: '[0-9]*',
          maxLength: selectedCountry.nationalNumberLength,
        }}
        disabled={disabled}
        {...textFieldProps}
      />
    </Box>
  );
};

export default PhoneNumberInput;
